import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { PORTFOLIO, PORTFOLIO_FILTERS } from "@/data/site";

const Item = ({ p }) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    data-testid={`portfolio-item-${p.tag.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={p.img}
        alt={`${p.title} — ${p.tag} website by RedKey Digital`}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
      <span className="absolute top-4 left-4 rounded-full bg-primary/90 text-primary-foreground text-xs font-semibold px-3 py-1">
        {p.tag}
      </span>
    </div>
    <div className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold text-lg tracking-tight">{p.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
        </div>
        <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-all group-hover:rotate-45 shrink-0 mt-1" />
      </div>
      {p.demo && (
        <a
          href={p.demo}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`portfolio-demo-${p.tag.toLowerCase().replace(/\s+/g, "-")}`}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary hover:bg-[#c81f1f] text-primary-foreground text-sm font-semibold px-4 py-2 transition-colors"
        >
          View Live Demo <ArrowUpRight className="w-4 h-4" />
        </a>
      )}
    </div>
  </motion.div>
);

export const PortfolioGrid = ({ heading = true }) => {
  const [filter, setFilter] = useState("All");
  const shown = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.tag === filter);

  return (
    <section id="portfolio" className="relative py-20 md:py-28 bg-muted border-y border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {heading && (
          <div className="max-w-2xl mb-10">
            <Reveal as="p" className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Portfolio</Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 text-4xl md:text-5xl font-heading font-bold tracking-tight">
                Websites that launch and rank
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-4 text-muted-foreground text-lg">
              A snapshot of the kinds of businesses we build for — from restaurants to law firms. (Sample demo projects.)
            </Reveal>
          </div>
        )}

        <Reveal className="flex flex-wrap gap-2 mb-8">
          {PORTFOLIO_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                filter === f ? "bg-primary text-primary-foreground" : "border border-border bg-white text-muted-foreground hover:text-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {shown.map((p) => <Item key={p.title} p={p} />)}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
